import { Typography } from '@mui/material';
import { PERFORMANCE_FEE_TEMPLATES } from 'adminConstants/routes';
import Heading from 'components/Heading';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageLoader from 'components/PageLoader';
import AddEditPerformanceFeeTemplateForm from './AddEditPerformanceFeeTemplateForm';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { performanceFeeTemplates as rules } from 'further-ui/validations';
import { Api } from 'further-types/performance-fee';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHandleTrpcQuery } from 'hooks/ui/useHandleTrpcQuery';
import { FeeHurdleBasis } from 'further-types/investment';
import { GrossOrNetFee } from 'further-types/performance-fee';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';

export interface FeeTemplateFormData {
  firmId?: string;
  feeTemplateName: string;
  hurdleBasis: FeeHurdleBasis;
  grossOrNetFees: GrossOrNetFee;
  excludeResidualCapitalFromHurdle?: boolean;
  performanceFees: Array<{
    feeName: string;
    hurdle: number;
    feePercentageIncVAT: number;
    feePercentageExVAT: number;
    vatPercentage: number;
  }>;
}

const AddEditPerformanceFeeTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const breadcrumbs = [
    { label: 'Dashboard' },
    { label: 'Performance fee templates', link: PERFORMANCE_FEE_TEMPLATES },
  ];

  const {
    data: performanceFeeTemplate,
    isLoading,
    error,
  } = api.performanceFeeTemplates.getById.useQuery({ id }, { enabled: !!id });

  useHandleTrpcQuery({ error, errorRedirect: PERFORMANCE_FEE_TEMPLATES });

  const values: FeeTemplateFormData = {
    feeTemplateName: '',
    firmId: '',
    hurdleBasis: FeeHurdleBasis.Company,
    grossOrNetFees: GrossOrNetFee.Net,
    excludeResidualCapitalFromHurdle: false,
    performanceFees: [],
  };

  const formMethods = useForm({
    shouldFocusError: true,
    resolver: zodResolver(rules.CreateOrEditPerformanceFeeTemplateInput),
    criteriaMode: 'all',
    values,
    mode: 'onChange',
  });

  useEffect(() => {
    if (performanceFeeTemplate) {
      const values = {
        feeTemplateName: performanceFeeTemplate.name,
        firmId: performanceFeeTemplate.firmId || '',
        templateInUse: performanceFeeTemplate.templateInUse,
        hurdleBasis: performanceFeeTemplate.hurdleBasis,
        grossOrNetFees: performanceFeeTemplate.includeFeesInHurdle
          ? GrossOrNetFee.Net
          : GrossOrNetFee.Gross,
        excludeResidualCapitalFromHurdle:
          performanceFeeTemplate.excludeResidualCapitalFromHurdle ?? false,
        performanceFees: performanceFeeTemplate.performanceFees.map(
          (performanceFee) => ({
            feeName: performanceFee.feeName,
            hurdle: performanceFee.hurdle,
            feePercentageIncVAT: performanceFee.feePercentageIncVAT,
            feePercentageExVAT: performanceFee.feePercentageExVAT,
            vatPercentage: performanceFee.vatPercentage,
          }),
        ),
      };
      formMethods.reset(values);
    }
  }, [performanceFeeTemplate, formMethods.reset]);

  const {
    mutate: createPerformanceFeeTemplate,
    isPending: createPerformanceFeeTemplatePending,
  } = api.performanceFeeTemplates.create.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Performance fee template created successfully',
      successRedirect: PERFORMANCE_FEE_TEMPLATES,
    }),
  );

  const {
    mutate: updatePerformanceFeeTemplate,
    isPending: updatePerformanceFeeTemplatePending,
  } = api.performanceFeeTemplates.update.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Performance fee template updated successfully',
      successRedirect: PERFORMANCE_FEE_TEMPLATES,
    }),
  );

  const saveFeeTemplate = formMethods.handleSubmit(
    (data: Api.CreateOrEditPerformanceFeeTemplateRequest) => {
      if (isEdit) {
        updatePerformanceFeeTemplate({
          ...data,
          id: performanceFeeTemplate?._id,
          nameOnlyUpdate: performanceFeeTemplate?.templateInUse,
        });
      } else {
        createPerformanceFeeTemplate(data);
      }
    },
  );

  const isEdit = !!performanceFeeTemplate;

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <PageContainer
      heading={`Fee manager: ${
        isEdit ? 'Edit' : 'Add'
      } performance fee template`}
      breadcrumbs={breadcrumbs}
    >
      <PageContent withSpacing>
        <Heading variant="h2" noMargin>
          {isEdit ? 'Edit' : 'Add'} performance fee template
        </Heading>
        <Typography variant="body2" color="text.secondary">
          Use this section to {isEdit ? 'edit an existing' : 'add a new'}{' '}
          performance fee template, which can be applied to a tranche by default
          or to individual investments.
        </Typography>

        {(!id || performanceFeeTemplate) && (
          <FormProvider {...formMethods}>
            <form onSubmit={saveFeeTemplate}>
              <AddEditPerformanceFeeTemplateForm
                isLoading={
                  createPerformanceFeeTemplatePending ||
                  updatePerformanceFeeTemplatePending
                }
                templateInUse={performanceFeeTemplate?.templateInUse}
                isEdit={isEdit}
              />
            </form>
          </FormProvider>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default AddEditPerformanceFeeTemplate;
